import { APIError, NotFoundError, RateLimitError, AuthenticationError } from "openai";
import type { ResponseInputItem } from "openai/resources/responses/responses";
import type { CollectedPromptResponse } from "./invoke.js";
import { openAIErrorStatus } from "./openai-error.js";

// ── Wire contracts ──────────────────────────────────────────────
export interface InvokeRequest {
  projectEndpoint: string;
  agentName: string;
  version?: string; // defensive pin to this worker (NOT an arm selector)
  conversationId?: string; // omit -> cli creates a FRESH conversation
  prompt: string;
  brain?: {
    connectionName: string;
    installationId: string;
    repo: string;
    projectArmId: string;
    kvUri: string;
    /** Omitted is the legacy GitHub MCP and retains rotate-before-invoke behavior. */
    transport?: "gateway-mcp-v1" | "github-mcp";
  };
}

export type InvokeReason = "content_filter" | "auth" | "not_found" | "rate_limit" | "transient" | "unknown";

export interface InvokeResult {
  ok: boolean;
  text?: string;
  responseId?: string;
  model?: string;
  usage?: { inputTokens: number; outputTokens: number; totalTokens: number };
  toolTrace?: string[];
  durationMs: number;
  conversationId: string;
  // error path:
  retryable?: boolean;
  status?: number;
  reason?: InvokeReason;
  message?: string;
}

// ── Error classification (mirrors mapOpenAIError + content-filter/abort/network) ─
export interface ErrorClass {
  reason: InvokeReason;
  retryable: boolean;
  status?: number;
  message: string;
}

function isBrainConnPropagation(err: unknown): boolean {
  // Fresh-connection credential-propagation lag: right after a brain connection is
  // created/rotated, the GitHub hosted MCP gateway may not yet see the new installation
  // token, so tool-enumeration fails with a 400 whose body says the Authorization header
  // is "badly formatted" / a bad MCP request. A few seconds later (the next backoff
  // attempt re-rotates and the gateway has caught up) it resolves — so this is a
  // RETRYABLE transient, NOT a hard auth fault. Message-based because the fault surfaces
  // as a generic upstream 400 (no openai SDK error subclass).
  const e = err as { message?: string; error?: { message?: string } };
  const msg = `${e.error?.message ?? ""} ${e.message ?? ""}`.toLowerCase();
  return (
    msg.includes("enumerating tools from remote server") ||
    msg.includes("authorization header is badly formatted") ||
    (msg.includes("mcp") && msg.includes("bad request"))
  );
}

function isRateLimitMessage(err: unknown): boolean {
  const e = err as { message?: string; error?: { message?: string } };
  const msg = `${e.error?.message ?? ""} ${e.message ?? ""}`.toLowerCase();
  return /rate limit|exceeded .*\brate\b|too many requests|\b429\b/.test(msg);
}

function isTransientServerMessage(err: unknown): boolean {
  // Azure/OpenAI internal server faults that arrive WITHOUT a clean status>=500 — e.g. a
  // mid-stream 500 ("The server had an error processing your request. Sorry about that!"),
  // a just-created agent version invoked before it is fully provisioned, or an upstream
  // hiccup. These are retryable transients; the backoff absorbs them.
  const e = err as { message?: string; error?: { message?: string } };
  const msg = `${e.error?.message ?? ""} ${e.message ?? ""}`.toLowerCase();
  return (
    msg.includes("the server had an error processing your request") ||
    msg.includes("internal server error") ||
    msg.includes("server_error") ||
    msg.includes("service unavailable") ||
    msg.includes("temporarily unavailable") ||
    msg.includes("overloaded") ||
    msg.includes("please retry") ||
    msg.includes("try again later")
  );
}

function isContentFilter(err: unknown): boolean {
  // Azure surfaces RAI blocks as a 400 whose body carries code "content_filter"
  // (or message text from the content-management policy). Deterministic
  // re-filter -> non-retryable, becomes a task-authoring finding, never a flaky FAIL.
  const code = (err as { code?: string }).code;
  if (code === "content_filter") return true;
  const body = (err as { error?: { code?: string; message?: string } }).error;
  if (body?.code === "content_filter") return true;
  const msg = (body?.message ?? (err as { message?: string }).message ?? "").toLowerCase();
  return msg.includes("content management policy") || msg.includes("content_filter") || msg.includes("responsibleaipolicyviolation");
}

export function classifyError(err: unknown): ErrorClass {
  // Rate-limit messages win over the generic transient path: a streamed "exceeded rate limit
  // … please retry" would otherwise match isTransientServerMessage via "please retry".
  if (isRateLimitMessage(err)) {
    const status = (err as { status?: number }).status;
    return { reason: "rate_limit", retryable: true, status, message: (err as { message?: string }).message ?? String(err) };
  }
  // Brain-connection propagation lag wins over the generic 400/unknown paths: it is a
  // transient the backoff must absorb (first invoke after a fresh connection link).
  if (isBrainConnPropagation(err) || isTransientServerMessage(err)) {
    const status = (err as { status?: number }).status;
    return { reason: "transient", retryable: true, status, message: (err as { message?: string }).message ?? String(err) };
  }
  // instanceof alone misses a foreign-copy-shaped error: the workspace resolves
  // one copy of `openai` while @azure/ai-projects nests another, and a client
  // built through the Azure SDK throws the nested copy's classes. status is
  // duck-typed via openAIErrorStatus so either copy classifies identically.
  const status = openAIErrorStatus(err);
  const errMessage = err instanceof Error ? err.message : String(err);
  if (err instanceof NotFoundError || status === 404) {
    return { reason: "not_found", retryable: false, status, message: errMessage };
  }
  if (err instanceof RateLimitError || status === 429) {
    return { reason: "rate_limit", retryable: true, status, message: errMessage };
  }
  if (err instanceof AuthenticationError || status === 401) {
    // 401 AFTER a fresh rotation is a real auth/config fault — non-retryable.
    return { reason: "auth", retryable: false, status, message: errMessage };
  }
  if (err instanceof APIError || status !== undefined) {
    if (isContentFilter(err)) {
      return { reason: "content_filter", retryable: false, status, message: errMessage };
    }
    if (status !== undefined && status >= 500) {
      return { reason: "transient", retryable: true, status, message: errMessage };
    }
    return { reason: "unknown", retryable: false, status, message: errMessage };
  }
  if (err instanceof Error) {
    // Stream abort/timeout + network faults are transient (retryable set).
    if (err.name === "AbortError" || /aborted|timeout|ECONNRESET|ECONNREFUSED|ETIMEDOUT|socket hang up|network/i.test(err.message)) {
      return { reason: "transient", retryable: true, message: err.message };
    }
    return { reason: "unknown", retryable: false, message: err.message };
  }
  return { reason: "unknown", retryable: false, message: String(err) };
}

/**
 * Every classification the CLI can put on `.error.category`.
 *
 * Declared as a const tuple, not a bare union, so it can be ITERATED at runtime.
 * A bare union erases at compile time, which means nothing can check that a
 * downstream consumer handles every member - and the installer's bash mapper is
 * exactly such a consumer. Adding a category here makes that coverage test fail
 * until the mapper handles it, which is the point.
 */
export const FOUNDRY_ERROR_CATEGORIES = [
  "data_plane_not_ready", "provisioning_error", "image_pull", "rbac", "auth",
  "content_filter", "rate_limit", "transient", "not_found", "unknown",
] as const;

export type FoundryErrorCategory = (typeof FOUNDRY_ERROR_CATEGORIES)[number];

export interface FoundryErrorClass {
  category: FoundryErrorCategory;
  retryable: boolean;
  status?: number;
  message: string;
}

function errText(err: unknown): string {
  const e = err as { message?: string; error?: { message?: string; code?: string }; code?: string };
  return `${e.code ?? ""} ${e.error?.code ?? ""} ${e.error?.message ?? ""} ${e.message ?? ""}`.trim();
}

function errStatus(err: unknown): number | undefined {
  const direct = openAIErrorStatus(err);
  if (direct !== undefined) return direct;
  const e = err as { upstreamStatus?: number; message?: string };
  if (typeof e.upstreamStatus === "number") return e.upstreamStatus;
  const m = /\b(4\d\d|5\d\d)\b/.exec(e.message ?? "");
  return m ? Number(m[1]) : undefined;
}

/**
 * Richer-than-`classifyError` taxonomy used by the installer (via the CLI JSON envelope),
 * the web UI (on the flattened `chat.error`), and `m8t foundry await-ready`. Message/status/code
 * aware so it works on an openai SDK error, a CLI LocalCliError, OR a flattened client Error.
 * Priority order matters: data-plane-not-ready / provisioning / image-pull must win over the
 * generic 404/4xx buckets.
 */
export function classifyFoundryError(err: unknown): FoundryErrorClass {
  const text = errText(err).toLowerCase();
  const status = errStatus(err);
  const message = (err as { message?: string }).message ?? String(err);

  // 1. Transient data-plane not-ready: a fresh/unstable project serves 404 "Project not found".
  //    THE headline retryable case. Message-based so it survives the server→client wire.
  if (text.includes("project not found") || (status === 404 && /data.?plane|not ready/.test(text))) {
    return { category: "data_plane_not_ready", retryable: true, status, message };
  }
  // 2. Image / registry pull failure (6/25 ACR cutoff class). NON-retryable — surface clearly.
  if (/imageerror|container registry authentication|image_pull|registrynotfound|unauthorizedacrpull/.test(text)) {
    return { category: "image_pull", retryable: false, status, message };
  }
  // 3. Platform provisioning failure. Retryable but LOW budget at the consumer (HANDOFF: retries
  //    rarely help) — the value is the honest fail-clear when exhausted, never a blind loop.
  if (/version_failed|provisioningerror|provisioning failed|status 'failed'/.test(text)) {
    return { category: "provisioning_error", retryable: true, status, message };
  }
  // 4. Genuine RBAC grant propagation (403). Retryable with backoff (minutes).
  if (status === 403 || /permissiondenied|forbiddenbyrbac|\bforbidden\b/.test(text)) {
    return { category: "rbac", retryable: true, status, message };
  }
  // 5. Content filter — deterministic, non-retryable.
  if (isContentFilter(err)) return { category: "content_filter", retryable: false, status, message };
  // 6. Rate limit.
  if (err instanceof RateLimitError || status === 429) return { category: "rate_limit", retryable: true, status, message };
  // 7. Auth (401 after a fresh rotation = real fault).
  if (err instanceof AuthenticationError || status === 401) return { category: "auth", retryable: false, status, message };
  // 8. KNOWN transient server messages, and brain-connection propagation lag.
  //     These keep the precedence they have always had, ABOVE not_found: a server
  //     that says "please retry" or "temporarily unavailable" is describing a
  //     transient whatever status it attaches to it. They were briefly demoted
  //     below not_found as a side effect of moving the NETWORK regex, which is a
  //     different question — a 404 + "please retry" would have become
  //     non-retryable, and `retry_classified` has no not_found arm, so that is a
  //     hard install failure rather than a retry. Reachability unproven; the
  //     precedence is restored rather than argued about.
  if (isBrainConnPropagation(err) || isTransientServerMessage(err)) {
    return { category: "transient", retryable: true, status, message };
  }
  // 9. Genuine not-found (a real missing resource — project-not-found already
  //    excluded at rule 1). ABOVE the network arm, deliberately: a 404 body that
  //    happens to quote "timed out" must stay non-retryable, and ORDER is how that
  //    is expressed. It was briefly a `status === undefined` gate on the arm below
  //    instead, which was wrong in a way worth recording — `status` here is not an
  //    HTTP status but one SCRAPED from the message by errStatus's /\b(4\d\d|5\d\d)\b/,
  //    so "connect ETIMEDOUT 20.51.104.51:443" carries status 443 and an HTTP 408
  //    carries 408. Both were made non-retryable by that gate; measured, both are
  //    retryable again with ordering doing the work.
  if (err instanceof NotFoundError || status === 404) return { category: "not_found", retryable: false, status, message };
  // 10. Other transients (5xx / network / abort).
  //
  // THE NETWORK REGEX RUNS ON `text`, NOT ON `err.message`, and that is the whole
  // point: `err instanceof Error` is FALSE for the shape the pollers actually hand
  // in. `awaitDataPlaneReady` calls this with a plain `{ status, message }` it built
  // from a probe's catch, so an Error-gated arm never saw a timeout at all and one
  // classified `unknown` — non-retryable — which makes the readiness poll THROW a
  // fatal on a single slow request. Measured, not reasoned: a flattened
  // "aborted due to timeout" returned category `unknown` before this line moved.
  // Same reason rule 1 is message-based — these have to survive the wire.
  if (
    (status !== undefined && status >= 500) ||
    (err instanceof Error && err.name === "AbortError") ||
    /aborted|timeout|timed out|econnreset|econnrefused|etimedout|socket hang up|network/i.test(text)
  ) {
    return { category: "transient", retryable: true, status, message };
  }
  // 11. Unknown.
  return { category: "unknown", retryable: false, status, message };
}

// ── Injected impure boundary (cloud I/O) — mocked in tests ────────────────────
export interface InvokeDeps {
  /** Rotate the brain connection token (rotateConnectionAuth). No-op caller passes when no brain. */
  rotate: (brain: NonNullable<InvokeRequest["brain"]>) => Promise<void>;
  /** Build the OpenAI client bound to the project endpoint. */
  makeClient: (projectEndpoint: string) => unknown;
  /** Create a fresh Foundry conversation, returning its id. */
  createConversation: (client: unknown, agentName: string) => Promise<string>;
  /** The pure streaming invoke (collectPromptResponse), client pre-bound by makeClient. */
  invoke: (
    client: unknown,
    args: { agentName: string; version?: string; conversationId: string; input: ResponseInputItem[] },
  ) => Promise<CollectedPromptResponse>;
}

/**
 * Rotate-THEN-invoke, atomically, one turn. Pure orchestration: all cloud I/O is
 * in `deps`. Never throws — every fault becomes a classified failure InvokeResult
 * so the Python runner decides retry from the `reason`/`retryable` fields alone.
 */
export async function runInvoke(req: InvokeRequest, deps: InvokeDeps): Promise<InvokeResult> {
  const t0 = Date.now();
  let conversationId = req.conversationId ?? "";
  try {
    // MAJOR#1: the AIProjectClient needs a project endpoint. Prefer the request value (threaded
    // end-to-end from the Python runner's _build_request); fall back to $EXAM_PROJECT_ENDPOINT so an
    // un-threaded request still resolves rather than failing on an empty URL.
    // Empty-string (un-threaded) request falls back to the env; `??` alone wouldn't,
    // since projectEndpoint is a required string that can be "" but never null/undefined.
    const projectEndpoint = req.projectEndpoint !== "" ? req.projectEndpoint : (process.env.EXAM_PROJECT_ENDPOINT ?? "");
    const client = deps.makeClient(projectEndpoint);
    // Gateway-MCP bearers are persistent random credentials provisioned at link
    // time. Rotating one as if it were a legacy GitHub installation token would
    // overwrite the bearer and make the gateway reject every subsequent call.
    // Omitted transport remains legacy for backward compatibility.
    if (req.brain && req.brain.transport !== "gateway-mcp-v1") {
      await deps.rotate(req.brain); // legacy GitHub MCP: rotate BEFORE invoke
    }
    if (!conversationId) conversationId = await deps.createConversation(client, req.agentName);

    const collected = await deps.invoke(client, {
      agentName: req.agentName,
      version: req.version,
      conversationId,
      input: [{ role: "user", content: req.prompt }],
    });

    return {
      ok: true,
      text: collected.text,
      responseId: collected.responseId,
      model: collected.model,
      usage: collected.usage,
      toolTrace: collected.toolTrace,
      durationMs: Date.now() - t0,
      conversationId,
    };
  } catch (err) {
    const c = classifyError(err);
    return {
      ok: false,
      reason: c.reason,
      retryable: c.retryable,
      status: c.status,
      message: c.message,
      durationMs: Date.now() - t0,
      conversationId,
    };
  }
}
